import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';

import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { createArticleBatchSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleBatchSlice';
import { createArticleEventHandlerSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleEventHandlerSlice';
import { createArticleGroupSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleGroupSlice';
import { createArticleSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleSlice';
import { createArticleTransformationSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleTransformationSlice';
import { createCameraSlice } from 'app/modules/wallforming/components/state-management/placement/slices/cameraSlice';
import { createColliderSlice } from 'app/modules/wallforming/components/state-management/placement/slices/colliderSlice';
import { createConnectionSlice } from 'app/modules/wallforming/components/state-management/placement/slices/connectionSlice';
import { createDevToolsSlice } from 'app/modules/wallforming/components/state-management/placement/slices/devToolsSlice';
import { createHelperSlice } from 'app/modules/wallforming/components/state-management/placement/slices/helperSlice';
import { createInputBindingSlice } from 'app/modules/wallforming/components/state-management/placement/slices/inputBindingSlice';
import { createPoiSlice } from 'app/modules/wallforming/components/state-management/placement/slices/poiSlice';
import { createProjectsSlice } from 'app/modules/wallforming/components/state-management/placement/slices/projectsSlice';
import { createSceneEventHandlerSlice } from 'app/modules/wallforming/components/state-management/placement/slices/scenes/sceneEventHandlerSlice';
import { createSceneSlice } from 'app/modules/wallforming/components/state-management/placement/slices/scenes/sceneSlice';
import { createUiSlice } from 'app/modules/wallforming/components/state-management/placement/slices/uiSlice';
import {
    bindHistoryActions,
    createPlacementHistory
} from 'app/modules/wallforming/components/state-management/placement/utils/placementHistory';
import { trackWorkspaceRevision } from 'app/modules/wallforming/components/state-management/placement/utils/workspaceRevision';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

type PlacementPersistedState = Pick<
    PlacementState,
    | 'selectedSettingType'
    | 'settingTypeFilter'
    | 'selectedSettingView'
    | 'selectedArticleId'
    | 'poiSelection'
    | 'interactionMode'
    | 'disableCameraAlignBySource'
    | 'autoPlaceSupportingConstructionClamps'
    | 'mainCameraProjectionType'
> & {
    dimensionSettings: Partial<PlacementState['dimensionSettings']> & { showDetails?: boolean; enabled?: boolean };
};

const placementPersistStorageKey = 'wallforming-placement-state';

const placementPersistStorage = createJSONStorage<PlacementPersistedState>(() => sessionStorage);

export const usePlacementStore = create<PlacementState>()(
    persist(
        subscribeWithSelector((set, get, api) => {
            const state: PlacementState = {
                ...createUiSlice(set, get, api),
                ...createPoiSlice(set, get, api),
                ...createArticleSlice(set, get, api),
                ...createArticleTransformationSlice(set, get, api),
                ...createArticleEventHandlerSlice(set, get, api),
                ...createArticleBatchSlice(set, get, api),
                ...createArticleGroupSlice(set, get, api),
                ...createProjectsSlice(set, get, api),
                ...createSceneSlice(set, get, api),
                ...createHelperSlice(set, get, api),
                ...createCameraSlice(set, get, api),
                ...createConnectionSlice(set, get, api),
                ...createSceneEventHandlerSlice(set, get, api),
                ...createColliderSlice(set, get, api),
                ...createInputBindingSlice(set, get, api),
                ...createDevToolsSlice(set, get, api),
                workspaceRevision: 0,
                ...createPlacementHistory(api)
            };
            bindHistoryActions(state);
            trackWorkspaceRevision(api, set);
            return state;
        }),
        {
            name: placementPersistStorageKey,
            storage: placementPersistStorage,
            partialize: (state): PlacementPersistedState => ({
                settingTypeFilter: state.settingTypeFilter,
                selectedSettingType: state.selectedSettingType,
                selectedSettingView: state.selectedSettingView ?? SettingView.AUTOMATED_PLACEMENT,
                selectedArticleId: state.selectedArticleId,
                poiSelection: state.poiSelection,
                interactionMode: state.interactionMode,
                disableCameraAlignBySource: state.disableCameraAlignBySource,
                autoPlaceSupportingConstructionClamps: state.autoPlaceSupportingConstructionClamps,
                mainCameraProjectionType: state.mainCameraProjectionType,
                dimensionSettings: state.dimensionSettings
            }),
            merge: (persisted, current) => {
                const persistedState = (persisted ?? {}) as Partial<PlacementPersistedState>;
                const { showDetails, enabled, ...dimensionSettings } = persistedState.dimensionSettings ?? {};
                return {
                    ...current,
                    ...persistedState,
                    poiSelection: {
                        current: {
                            ...(persistedState.poiSelection ?? current.poiSelection).current,
                            source: undefined,
                            shouldRealignCamera: undefined
                        }
                    },
                    // nested merge keeps newly added dimension settings when older session data is restored
                    dimensionSettings: {
                        ...current.dimensionSettings,
                        ...dimensionSettings,
                        visible: dimensionSettings.visible ?? enabled ?? current.dimensionSettings.visible,
                        showConnectors: dimensionSettings.showConnectors ?? showDetails ?? current.dimensionSettings.showConnectors,
                        showAnchors: dimensionSettings.showAnchors ?? showDetails ?? current.dimensionSettings.showAnchors,
                        chains: {
                            ...current.dimensionSettings.chains,
                            ...dimensionSettings.chains
                        }
                    }
                };
            }
        }
    )
);
